// src/pages/Settings/PersonnelMenu.jsx

import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, updateEmail } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { requestNotificationPermissionAndSaveToken, removeNotificationToken } from '../../utils/notificationUtils';

const styles = {
    container: {
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '20px'
    },
    header: {
        textAlign: 'center',
        marginBottom: '30px',
        color: '#333',
        fontSize: '28px',
        fontWeight: '700'
    },
    card: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        padding: '30px',
        marginBottom: '30px',
        border: '1px solid #eef2f7'
    },
    title: {
        fontSize: '22px',
        fontWeight: '600',
        marginBottom: '25px',
        color: '#212529',
        paddingBottom: '15px',
        borderBottom: '2px solid #f0f4f8'
    },
    formGroup: {
        marginBottom: '25px'
    },
    label: {
        display: 'block',
        marginBottom: '10px',
        fontWeight: '500',
        color: '#495057',
        fontSize: '15px'
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '16px',
        border: '1px solid #ced4da',
        borderRadius: '8px',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        ':focus': {
            borderColor: '#06C167',
            boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
            outline: 'none'
        }
    },
    button: {
        padding: '14px 24px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: '#06C167',
        color: 'white',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        marginRight: '12px',
        transition: 'background-color 0.2s',
        ':hover': {
            backgroundColor: '#4f46e5'
        },
        ':disabled': {
            backgroundColor: '#a5b4fc',
            cursor: 'not-allowed'
        }
    },
    cancelButton: {
        padding: '14px 24px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        backgroundColor: 'transparent',
        color: '#6b7280',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'background-color 0.2s, color 0.2s',
        ':hover': {
            backgroundColor: '#f9fafb',
            color: '#374151'
        }
    },
    error: {
        color: '#dc2626',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        padding: '14px',
        marginTop: '15px',
        fontSize: '15px'
    },
    success: {
        color: '#166534',
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '8px',
        padding: '14px',
        marginTop: '15px',
        fontSize: '15px'
    },
    section: {
        marginBottom: '30px'
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '600',
        marginBottom: '15px',
        color: '#333',
        paddingBottom: '10px',
        borderBottom: '1px solid #eee'
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: '500',
        color: '#495057',
        fontSize: '15px',
        cursor: 'pointer',
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        transition: 'background-color 0.2s, border-color 0.2s',
        ':hover': {
            backgroundColor: '#f9fafb',
            borderColor: '#d1d5db'
        }
    },
    checkbox: {
        marginRight: '12px',
        width: '18px',
        height: '18px',
        cursor: 'pointer'
    }
};

function PersonnelMenu() {
    const { currentUser, userData } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const [loading, setLoading] = useState(false);

    // Profil bilgileri için state
    const [profileData, setProfileData] = useState({
        displayName: userData?.displayName || '',
        phone: userData?.phone || ''
    });

    // Bildirim ayarları için state
    const [notificationSettings, setNotificationSettings] = useState({
        emailNotifications: userData?.emailNotifications !== false, // Varsayılan olarak açık
        pushNotifications: userData?.pushNotifications !== false
    });

    // userData değiştiğinde profil verilerini güncelle
    React.useEffect(() => {
        setProfileData({
            displayName: userData?.displayName || '',
            phone: userData?.phone || ''
        });

        setNotificationSettings({
            emailNotifications: userData?.emailNotifications !== false,
            pushNotifications: userData?.pushNotifications !== false
        });
    }, [userData]);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        // Clear any previous messages that aren't related to this form
        if (message && !message.includes('Şifre') && !message.includes('şifre')) {
            setMessage('');
        }

        // Şifre doğrulama
        if (newPassword !== confirmPassword) {
            setMessage('Yeni şifreler eşleşmiyor.');
            setMessageType('error');
            return;
        }

        if (newPassword.length < 6) {
            setMessage('Şifre en az 6 karakter olmalıdır.');
            setMessageType('error');
            return;
        }

        if (currentPassword === newPassword) {
            setMessage('Yeni şifre, mevcut şifreden farklı olmalıdır.');
            setMessageType('error');
            return;
        }

        setLoading(true);

        try {
            // Kullanıcıyı yeniden kimlik doğrulama
            const credential = EmailAuthProvider.credential(
                currentUser.email,
                currentPassword
            );

            await reauthenticateWithCredential(currentUser, credential);

            // Şifre güncelleme
            await updatePassword(currentUser, newPassword);

            setMessage('Şifreniz başarıyla güncellendi.');
            setMessageType('success');

            // Formu temizle
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error('Şifre değiştirme hatası:', error);
            let errorMessage = 'Şifre değiştirme işlemi sırasında bir hata oluştu.';

            if (error.code === 'auth/wrong-password') {
                errorMessage = 'Mevcut şifrenizi yanlış girdiniz.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = 'Şifre çok zayıf. Lütfen daha güçlü bir şifre seçin.';
            } else if (error.code === 'auth/requires-recent-login') {
                errorMessage = 'Güvenlik nedeniyle lütfen tekrar giriş yapın ve ardından şifre değişikliği yapın.';
            }

            setMessage(errorMessage);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        // Clear any previous messages that aren't related to this form
        if (message && !message.includes('Profil')) {
            setMessage('');
        }

        // Basic validation
        if (!profileData.displayName.trim()) {
            setMessage('Ad Soyad alanı boş bırakılamaz.');
            setMessageType('error');
            return;
        }

        // Phone number validation (if provided)
        if (profileData.phone && profileData.phone.trim() && !/^[0-9\s\-\+\(\)]+$/.test(profileData.phone)) {
            setMessage('Lütfen geçerli bir telefon numarası girin.');
            setMessageType('error');
            return;
        }

        setLoading(true);

        try {
            // Firestore'da kullanıcı belgesini güncelle
            const userRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userRef, {
                displayName: profileData.displayName.trim(),
                phone: profileData.phone.trim() || ''
            });

            setMessage('Profil bilgileriniz başarıyla güncellendi.');
            setMessageType('success');
        } catch (error) {
            console.error('Profil güncelleme hatası:', error);
            let errorMessage = 'Profil bilgileri güncellenirken bir hata oluştu.';

            // More specific error messages
            if (error.code === 'permission-denied') {
                errorMessage = 'Profil bilgilerinizi güncelleme yetkiniz bulunmamaktadır.';
            } else if (error.code === 'not-found') {
                errorMessage = 'Kullanıcı bilgileri bulunamadı.';
            } else if (error.code === 'unavailable') {
                errorMessage = 'Sunucuya şu anda erişilemiyor. Lütfen daha sonra tekrar deneyin.';
            }

            setMessage(errorMessage);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationSettingsUpdate = async (e) => {
        e.preventDefault();
        // Clear any previous messages that aren't related to this form
        if (message && !message.includes('Bildirim')) {
            setMessage('');
        }

        setLoading(true);

        try {
            // Firestore'da kullanıcı belgesini güncelle
            const userRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userRef, {
                emailNotifications: notificationSettings.emailNotifications,
                pushNotifications: notificationSettings.pushNotifications
            });

            // Handle FCM token based on the new setting
            if (notificationSettings.pushNotifications) {
                await requestNotificationPermissionAndSaveToken(currentUser.uid);
            } else {
                await removeNotificationToken(currentUser.uid);
            }

            setMessage('Bildirim ayarlarınız başarıyla güncellendi.');
            setMessageType('success');
        } catch (error) {
            console.error('Bildirim ayarları güncelleme hatası:', error);
            let errorMessage = 'Bildirim ayarları güncellenirken bir hata oluştu.';

            // More specific error messages
            if (error.code === 'permission-denied') {
                errorMessage = 'Bildirim ayarlarınızı güncelleme yetkiniz bulunmamaktadır.';
            } else if (error.code === 'not-found') {
                errorMessage = 'Kullanıcı bilgileri bulunamadı.';
            } else if (error.code === 'unavailable') {
                errorMessage = 'Sunucuya şu anda erişilemiyor. Lütfen daha sonra tekrar deneyin.';
            }

            setMessage(errorMessage);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.header}>Personel Menüsü</h2>

            <div style={styles.card}>
                <h3 style={styles.title}>Hesap Bilgileri</h3>
                <div style={styles.formGroup}>
                    <label style={styles.label}>E-posta Adresi</label>
                    <input
                        style={styles.input}
                        type="email"
                        value={userData?.email || ''}
                        readOnly
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>Firma</label>
                    <input
                        style={styles.input}
                        type="text"
                        value={userData?.companyName || ''}
                        readOnly
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>Rol</label>
                    <input
                        style={styles.input}
                        type="text"
                        value={userData?.role === 'admin' ? 'Yönetici' : 'Personel'}
                        readOnly
                    />
                </div>
            </div>

            <div style={styles.card}>
                <h3 style={styles.title}>Profil Bilgileri</h3>
                <form onSubmit={handleProfileUpdate}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ad Soyad</label>
                        <input
                            style={styles.input}
                            type="text"
                            value={profileData.displayName}
                            onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Telefon</label>
                        <input
                            style={styles.input}
                            type="text"
                            value={profileData.phone}
                            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        />
                    </div>

                    <button
                        type="submit"
                        style={styles.button}
                        disabled={loading}
                    >
                        Profil Bilgilerini Güncelle
                    </button>

                    {message && message.includes('Profil') && (
                        <div style={messageType === 'success' ? styles.success : styles.error}>
                            {message}
                        </div>
                    )}
                </form>
            </div>

            <div style={styles.card}>
                <h3 style={styles.title}>Şifre Değiştir</h3>
                <form onSubmit={handleChangePassword}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Mevcut Şifre</label>
                        <input
                            style={styles.input}
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Yeni Şifre</label>
                        <input
                            style={styles.input}
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Yeni Şifre (Tekrar)</label>
                        <input
                            style={styles.input}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        style={styles.button}
                        disabled={loading}
                    >
                        {loading ? 'İşleniyor...' : 'Şifre Değiştir'}
                    </button>

                    {message && !message.includes('Profil') && !message.includes('Bildirim') && (
                        <div style={messageType === 'success' ? styles.success : styles.error}>
                            {message}
                        </div>
                    )}
                </form>
            </div>

            <div style={styles.card}>
                <h3 style={styles.title}>Bildirim Ayarları</h3>
                <form onSubmit={handleNotificationSettingsUpdate}>
                    <div style={styles.formGroup}>
                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={notificationSettings.emailNotifications}
                                onChange={(e) => setNotificationSettings({ ...notificationSettings, emailNotifications: e.target.checked })}
                                style={styles.checkbox}
                            />
                            E-posta Bildirimleri
                        </label>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={notificationSettings.pushNotifications}
                                onChange={(e) => setNotificationSettings({ ...notificationSettings, pushNotifications: e.target.checked })}
                                style={styles.checkbox}
                            />
                            Anlık Bildirimler
                        </label>
                    </div>

                    <button
                        type="submit"
                        style={styles.button}
                        disabled={loading}
                    >
                        Bildirim Ayarlarını Güncelle
                    </button>

                    {message && message.includes('Bildirim') && (
                        <div style={messageType === 'success' ? styles.success : styles.error}>
                            {message}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default PersonnelMenu;